/**
 * My Marketing — Newsletters Supabase Data Access Layer
 *
 * Mirrors the conventions of automations.js: all queries speak snake_case to
 * the DB, camelCase transforms live here, and consuming hooks/components only
 * see camelCase.
 *
 * Reads/writes ride the anon client with app-enforced owner_id filtering
 * (Firebase Auth — no Supabase-auth RLS). Privileged operations (send now,
 * test send, stats) go through Cloud Functions callables instead.
 *
 * The audience preview filters here MUST stay in lockstep with the backend
 * resolver (functions/CRM/marketing/newsletterAudience.js) so the count a LO
 * sees is the count that actually sends.
 */

import { getSupabase } from '../../utils/supabase';

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

export function transformNewsletterToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    ownerId: row.owner_id,
    name: row.name,
    description: row.description,
    subject: row.subject,
    bodyHtml: row.body_html,
    replyTo: row.reply_to,
    sourceTemplateId: row.source_template_id,
    contentSource: row.content_source || 'custom',
    linkedTemplateId: row.linked_template_id,
    audienceConfig: row.audience_config || { contactTypes: ['real-estate-agent'], tagsAny: [] },
    scheduleConfig: row.schedule_config || {},
    status: row.status,
    nextRunAt: row.next_run_at,
    lastSentAt: row.last_sent_at,
    lastSkippedAt: row.last_skipped_at,
    lastSkipReason: row.last_skip_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function transformNewsletterToSnakeCase(newsletter) {
  const out = {};
  if (newsletter.ownerId !== undefined) out.owner_id = newsletter.ownerId;
  if (newsletter.name !== undefined) out.name = newsletter.name;
  if (newsletter.description !== undefined) out.description = newsletter.description;
  if (newsletter.subject !== undefined) out.subject = newsletter.subject;
  if (newsletter.bodyHtml !== undefined) out.body_html = newsletter.bodyHtml;
  if (newsletter.replyTo !== undefined) out.reply_to = newsletter.replyTo;
  if (newsletter.sourceTemplateId !== undefined) out.source_template_id = newsletter.sourceTemplateId;
  if (newsletter.contentSource !== undefined) out.content_source = newsletter.contentSource;
  if (newsletter.linkedTemplateId !== undefined) out.linked_template_id = newsletter.linkedTemplateId;
  if (newsletter.audienceConfig !== undefined) out.audience_config = newsletter.audienceConfig;
  if (newsletter.scheduleConfig !== undefined) out.schedule_config = newsletter.scheduleConfig;
  if (newsletter.status !== undefined) out.status = newsletter.status;
  if (newsletter.nextRunAt !== undefined) out.next_run_at = newsletter.nextRunAt;
  if (newsletter.lastSentAt !== undefined) out.last_sent_at = newsletter.lastSentAt;
  return out;
}

export function transformIssueStatsToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    newsletterId: row.newsletter_id,
    ownerId: row.owner_id,
    subject: row.subject,
    runKey: row.run_key,
    status: row.status,
    triggeredBy: row.triggered_by,
    audienceCount: row.audience_count,
    queuedCount: row.queued_count,
    sentCount: row.sent_count,
    failedCount: row.failed_count,
    dncSkippedCount: row.dnc_skipped_count,
    uniqueOpens: Number(row.unique_opens || 0),
    uniqueClicks: Number(row.unique_clicks || 0),
    totalOpens: Number(row.total_opens || 0),
    totalClicks: Number(row.total_clicks || 0),
    startedAt: row.started_at,
    completedAt: row.completed_at,
    createdAt: row.created_at,
  };
}

export function transformSendEngagementToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    issueId: row.issue_id,
    newsletterId: row.newsletter_id,
    contactId: row.contact_id,
    ownerId: row.owner_id,
    email: row.email,
    status: row.status,
    error: row.error,
    activityId: row.activity_id,
    sentAt: row.sent_at,
    createdAt: row.created_at,
    openCount: Number(row.open_count || 0),
    clickCount: Number(row.click_count || 0),
    firstOpenedAt: row.first_opened_at,
    firstClickedAt: row.first_clicked_at,
    // Enriched client-side (fetchIssueSends)
    contactName: row.contact_name,
  };
}

// ---------------------------------------------------------------------------
// Newsletters — CRUD
// ---------------------------------------------------------------------------

const NEWSLETTER_SELECT = 'id, owner_id, name, description, subject, body_html, reply_to, source_template_id, content_source, linked_template_id, audience_config, schedule_config, status, next_run_at, last_sent_at, last_skipped_at, last_skip_reason, created_at, updated_at';

export async function fetchNewsletters(ownerId, { includeArchived = false } = {}) {
  const sb = getSupabase();
  let query = sb
    .from('crm_newsletters')
    .select(NEWSLETTER_SELECT)
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (!includeArchived) {
    query = query.neq('status', 'archived');
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(transformNewsletterToCamelCase);
}

export async function fetchNewsletter(id) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_newsletters')
    .select(NEWSLETTER_SELECT)
    .eq('id', id)
    .single();
  if (error) throw error;
  return transformNewsletterToCamelCase(data);
}

export async function createNewsletter(newsletter) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_newsletters')
    .insert(transformNewsletterToSnakeCase(newsletter))
    .select(NEWSLETTER_SELECT)
    .single();
  if (error) throw error;
  return transformNewsletterToCamelCase(data);
}

export async function updateNewsletter(id, updates) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_newsletters')
    .update(transformNewsletterToSnakeCase(updates))
    .eq('id', id)
    .select(NEWSLETTER_SELECT)
    .single();
  if (error) throw error;
  return transformNewsletterToCamelCase(data);
}

export async function archiveNewsletter(id) {
  return updateNewsletter(id, { status: 'archived', nextRunAt: null });
}

// ---------------------------------------------------------------------------
// Issues & sends
// ---------------------------------------------------------------------------

export async function fetchNewsletterIssues(newsletterId, { limit = 50 } = {}) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('view_newsletter_issue_stats')
    .select('*')
    .eq('newsletter_id', newsletterId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(transformIssueStatsToCamelCase);
}

export async function fetchRecentIssues(ownerId, { limit = 25 } = {}) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('view_newsletter_issue_stats')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(transformIssueStatsToCamelCase);
}

/**
 * Per-recipient rows for one issue, with contact names attached (two-step —
 * the engagement view has no FK for PostgREST embedding).
 */
export async function fetchIssueSends(issueId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('view_newsletter_send_engagement')
    .select('*')
    .eq('issue_id', issueId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const sends = data || [];

  const contactIds = [...new Set(sends.map((s) => s.contact_id).filter(Boolean))];
  const nameById = new Map();
  for (let i = 0; i < contactIds.length; i += 200) {
    const chunk = contactIds.slice(i, i + 200);
    const { data: contacts, error: contactError } = await sb
      .from('crm_contacts')
      .select('id, first_name, last_name')
      .in('id', chunk);
    if (contactError) throw contactError;
    for (const c of contacts || []) {
      nameById.set(c.id, [c.first_name, c.last_name].filter(Boolean).join(' '));
    }
  }

  return sends.map((s) =>
    transformSendEngagementToCamelCase({ ...s, contact_name: nameById.get(s.contact_id) || null })
  );
}

// ---------------------------------------------------------------------------
// Audience preview — MUST mirror functions/CRM/marketing/newsletterAudience.js
// ---------------------------------------------------------------------------

export const DEFAULT_AUDIENCE_CONTACT_TYPES = ['real-estate-agent'];

function normalizeAudienceConfig(audienceConfig) {
  const cfg = audienceConfig || {};
  const contactTypes = Array.isArray(cfg.contactTypes)
    ? cfg.contactTypes.filter((v) => typeof v === 'string' && v.trim() !== '')
    : [];
  const tagsAny = Array.isArray(cfg.tagsAny)
    ? cfg.tagsAny.filter((v) => typeof v === 'string' && v.trim() !== '')
    : [];
  return {
    contactTypes: contactTypes.length > 0 ? contactTypes : [...DEFAULT_AUDIENCE_CONTACT_TYPES],
    tagsAny,
  };
}

function toPgArrayLiteral(values) {
  const escaped = values.map((v) => `"${String(v).replace(/"/g, '\\"')}"`);
  return `{${escaped.join(',')}}`;
}

function applyAudienceFilters(query, ownerId, audienceConfig) {
  const { contactTypes, tagsAny } = normalizeAudienceConfig(audienceConfig);
  let q = query
    .eq('owner_id', ownerId)
    .filter('contact_types', 'ov', toPgArrayLiteral(contactTypes))
    .not('email', 'is', null)
    .neq('email', '')
    .not('do_not_contact', 'is', true)
    .not('dnc_email', 'is', true)
    .not('is_active', 'is', false);
  if (tagsAny.length > 0) {
    q = q.filter('tags', 'ov', toPgArrayLiteral(tagsAny));
  }
  return q;
}

/** Live audience count for the editor/cards. */
export async function countAudience(ownerId, audienceConfig) {
  const sb = getSupabase();
  let query = sb.from('crm_contacts').select('id', { count: 'exact', head: true });
  query = applyAudienceFilters(query, ownerId, audienceConfig);
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

/** First N audience members for the editor's sample-recipient list. */
export async function sampleAudience(ownerId, audienceConfig, limit = 5) {
  const sb = getSupabase();
  let query = sb.from('crm_contacts').select('id, first_name, last_name, email, company');
  query = applyAudienceFilters(query, ownerId, audienceConfig);
  const { data, error } = await query.order('id', { ascending: true }).limit(limit);
  if (error) throw error;
  return (data || []).map((c) => ({
    id: c.id,
    firstName: c.first_name,
    lastName: c.last_name,
    name: [c.first_name, c.last_name].filter(Boolean).join(' '),
    email: c.email,
    company: c.company,
  }));
}
